import { useEffect, useRef, useState } from 'react';
import {
  Dialog, Box, Typography, Button, IconButton, Tooltip, useTheme, Zoom,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

interface DialogVisualizarPdfCaixaProps {
  open: boolean;
  titulo: string;
  blob: Blob | null;
  onClose: () => void;
}

// Visualizador embutido pros comprovantes de caixa (abertura, fechamento,
// sangria, extrato) — mostra o PDF dentro do próprio app, sem abrir outra
// aba do navegador. Imprimir aciona o diálogo nativo direto no iframe.
export const DialogVisualizarPdfCaixa: React.FC<DialogVisualizarPdfCaixaProps> = ({
  open, titulo, blob, onClose,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!blob) {
      setPdfUrl(null);
      return;
    }

    const url = URL.createObjectURL(blob);
    setPdfUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [blob]);

  const imprimir = () => {
    iframeRef.current?.contentWindow?.print();
  };

  const baixar = () => {
    if (!pdfUrl) return;

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `${titulo.toLowerCase().replace(/\s+/g, '-')}.pdf`;
    link.click();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 220 }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          overflow: 'hidden',
          height: '85vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `0.5px solid ${borderColor}`, flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <PictureAsPdfIcon sx={{ color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight={600} noWrap>{titulo}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          p: 1.5,
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
        }}
      >
        {pdfUrl ? (
          <iframe
            ref={iframeRef}
            src={pdfUrl}
            title={titulo}
            width="100%"
            height="100%"
            style={{ border: 'none', display: 'block', borderRadius: 8, backgroundColor: '#fff' }}
          />
        ) : (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.disabled">Gerando o documento...</Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          px: 2, py: 1.25, borderTop: `0.5px solid ${borderColor}`, display: 'flex',
          justifyContent: 'flex-end', gap: 1, flexShrink: 0,
        }}
      >
        <Tooltip title="Baixar PDF">
          <IconButton onClick={baixar} size="small" sx={{ border: `0.5px solid ${borderColor}` }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button variant="contained" size="small" startIcon={<PrintIcon fontSize="small" />} onClick={imprimir}>
          Imprimir
        </Button>
      </Box>
    </Dialog>
  );
};
